import { Popover } from "@headlessui/react";
import { captureEvent } from "@Light/app/analytics";
import { WithPopover } from "@Light/components/page/popover/WithPopover";
import { useLight } from "@Light/services/light.ts";
import { Calendar } from "@untitledui/icons";
import { twMerge } from "tailwind-merge";
import { DateSelector } from "./DateSelector";
import { getFullDate } from "./helpers";

export type DatepickerProps = {
  className?: string;
  label?: string;
  selectedDate: string;
  onChange: (v: string) => void;
  minSelectableDate: string;
  maxSelectableDate: string;
  onOpen?: () => void;
  onClose?: () => void;
  testId?: string;
};

export const Datepicker: React.FC<DatepickerProps> = ({
  className,
  label,
  selectedDate,
  onChange,
  minSelectableDate,
  maxSelectableDate,
  onOpen,
  onClose,
  testId,
}: DatepickerProps) => {
  const { useGetDisallowedDatesQuery } = useLight();

  const disallowedDatesResponse = useGetDisallowedDatesQuery();
  const disallowedDates =
    disallowedDatesResponse.data?.disallowed_dates.map((x) => x.date) ?? [];

  return (
    <WithPopover
      popover={
        <div className="p-6">
          <DateSelector
            selectedDate={selectedDate}
            onChange={onChange}
            minSelectableDate={minSelectableDate}
            maxSelectableDate={maxSelectableDate}
            disallowedDates={disallowedDates}
            isLoading={disallowedDatesResponse.isLoading}
          />
        </div>
      }
      onClose={onClose}
    >
      {({ open }) => {
        const onClickOpen = () => {
          captureEvent("enroll_select_date_open");
          onOpen?.();
          open();
        };

        return (
          <Popover.Button
            className={twMerge(
              "flex w-full items-center gap-2 rounded-md border border-secondary bg-primary px-3.25 py-2.25 shadow-xs",
              "text-md font-normal text-primary",
              "hover:border-primary",
              "focus:outline-none focus-visible:border-brand focus-visible:ring-4 focus-visible:ring-focus-ring/24",
              className,
            )}
            onClick={onClickOpen}
            data-testid={testId}
          >
            <Calendar className="size-5 shrink-0 text-fg-quaternary" />
            <div className="flex flex-col items-start">
              {label ? (
                <div className="text-xs font-normal text-secondary">
                  {label}
                </div>
              ) : null}
              <div>{getFullDate(selectedDate)}</div>
            </div>
          </Popover.Button>
        );
      }}
    </WithPopover>
  );
};
